import React, { useRef } from 'react';

/**
 * Drop-in-Ersatz für <input type="range"> mit smartem Touch-Handling:
 * Horizontal ziehen = Slider bewegen · Vertikal wischen = Seite scrollt normal weiter.
 * touch-action: pan-y sorgt dafür, dass der Browser vertikales Scrollen selbst übernimmt.
 */
export const TouchRangeInput = ({ min = 0, max = 100, step = 1, value, onChange, style, ...rest }) => {
  const ref = useRef(null);
  const gesture = useRef({ mode: 'idle', startX: 0, startY: 0 });

  const emitFromClientX = (clientX) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width) return;
    const nMin = Number(min);
    const nMax = Number(max);
    const nStep = Number(step) || 1;
    const ratio = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const raw = nMin + ratio * (nMax - nMin);
    const stepped = Math.min(Math.max(Math.round((raw - nMin) / nStep) * nStep + nMin, nMin), nMax);
    if (stepped !== Number(value)) onChange({ target: { value: String(stepped) } });
  };

  const handlePointerDown = (e) => {
    if (e.pointerType === 'mouse') return;
    gesture.current = { mode: 'pending', startX: e.clientX, startY: e.clientY };
  };

  const handlePointerMove = (e) => {
    const g = gesture.current;
    if (e.pointerType === 'mouse' || g.mode === 'idle' || g.mode === 'scroll') return;
    if (g.mode === 'pending') {
      const dx = Math.abs(e.clientX - g.startX);
      const dy = Math.abs(e.clientY - g.startY);
      if (dx < 6 && dy < 6) return;
      if (dy > dx) { g.mode = 'scroll'; return; }
      g.mode = 'drag';
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    }
    if (g.mode === 'drag') emitFromClientX(e.clientX);
  };

  const handlePointerUp = (e) => {
    if (e.pointerType !== 'mouse' && gesture.current.mode === 'pending') emitFromClientX(e.clientX);
    gesture.current.mode = 'idle';
  };

  // Live-Fill-Prozent für Neongrün-Progress im Track
  const nMin = Number(min);
  const nMax = Number(max);
  const nVal = Math.min(Math.max(Number(value), nMin), nMax);
  const pct = ((nVal - nMin) / (nMax - nMin || 1)) * 100;

  return (
    <input
      ref={ref}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={onChange}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => { gesture.current.mode = 'idle'; }}
      style={{ touchAction: 'pan-y', '--fill-pct': `${pct}%`, ...style }}
      {...rest}
    />
  );
};
